import React, { useEffect, useState, Suspense } from 'react';
import { createPortal } from 'react-dom';
import AppLoader from '../AppLoader/AppLoader';
import './BossMapModal.css';

/**
 * BossMapModal — bespoke map-viewport modal for boss encounters.
 *
 * Same portal-based pattern as DiamondMapModal: backdrop + panel + close
 * X, nothing else. BatchBossSession (rendered as children) brings its own
 * combat header, HP bars, boss identity, round badge, and question area
 * — all on a crimson accent. The modal just hands it the viewport.
 *
 * Scoped class prefix: `.bmm-*` so BatchBossSession's `.batch-*` rules
 * coexist cleanly.
 */
export default function BossMapModal({ node, onClose, children, targetSelector = null }) {
  // Reactive portal target: when this modal mounts via the
  // backgroundLocation pattern (e.g., LevelSession → mini-combat → /boss),
  // Dashboard's `.deck-map-area` may not be in the DOM yet at first
  // render — the dashboard route mounts in the same React commit. A
  // synchronous querySelector at render-time falls back to <body>, which
  // makes the modal effectively full-viewport (40px gutter) instead of
  // sitting inside the map area like Athero's regular-boss flow does.
  // useEffect runs AFTER commit, so by then both Dashboard and this modal
  // are in the DOM. A short retry loop catches the case where Dashboard
  // is still mounting when this effect first runs.
  const [target, setTarget] = useState(null);
  useEffect(() => {
    if (!node || typeof document === 'undefined') return undefined;
    let cancelled = false;
    let attempts = 0;
    let raf;
    const find = () => {
      if (cancelled) return;
      // Caller-supplied portal host wins (e.g. the mobile Drug-Lab demo's
      // full-screen fixed stage sits ON TOP of `.deck-map-area`, so combat
      // portaled there would render invisibly behind it). Default behavior
      // unchanged when targetSelector is not passed.
      const el = (targetSelector && document.querySelector(targetSelector))
        || document.querySelector('.deck-map-area')
        || document.body;
      setTarget(el);
      // Once we land on `.deck-map-area`, stop retrying. If Dashboard
      // hasn't mounted yet, retry up to ~1s of frames before settling
      // for body (which the existing `body > .bmm-overlay` CSS rule
      // already handles — but we prefer the map-area portal when it
      // becomes available).
      if (el === document.body && attempts < 60) {
        attempts += 1;
        raf = requestAnimationFrame(find);
      }
    };
    find();
    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
    };
  }, [node, targetSelector]);

  useEffect(() => {
    if (!node) return undefined;
    const onEsc = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [node, onClose]);

  if (!node) return null;

  // Modal-owned header (mirrors .mcp-header in MapCombatPanel). Inner
  // session's own combat-boss-info is hidden via CSS so the modal owns
  // the title chrome and the close X has no sticky competitor.
  const label = node.label || node.short_label || 'Encounter';
  // Subtitle is always "Encounter" — Edward's call: enemy name carries
  // the identity, the subtitle is just a label for the modal context.

  const panel = (
    <div
      className="bmm-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Boss fight: ${label}`}
    >
      <div
        className="bmm-body"
        data-modal-variant="boss"
        data-pool={node.pool || undefined}
        onClick={(e) => e.stopPropagation()}
      >
        {/* In-modal top region (X close + star avatar + boss name +
           "Encounter" subtitle) removed 2026-05-04 night per Edward —
           hides during the engaging/loading state too. Explanation
           popout styling is preserved by the .boss-session
           .diamond-feedback-explanation CSS alias in BossSession.css
           (commit 6ce8f1fb), independent of this header. */}

        <div className="bmm-content">
          <Suspense fallback={
            <div className="app-loader-shell app-loader-shell--panel">
              <AppLoader label="Loading boss encounter..." size="md" variant="boss" />
            </div>
          }>
            {children}
          </Suspense>
        </div>
      </div>
    </div>
  );

  if (!target) return null;
  return createPortal(panel, target);
}
